import DashboardService from "../../services/dashboard.service";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLayerGroup,
  faCircleCheck,
  faClock,
  faMagnifyingGlass,
  faFileLines,
  faEye,
  faUpload,
  faPlus,
  faBrain,
  faArrowRight,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import DonutChart from "../../components/Charts/DonutChart";
import QuestionGrowthChart, {
  mapGrowthResponse,
  QuestionGrowthData,
  RangeKey,
} from "../../components/Charts/QuestionGrowthChart";

const EMPTY_GROWTH: QuestionGrowthData = {
  series: [],
  totalAdded: 0,
  totalVerified: 0,
  verificationRate: 0,
};

export default function DashboardIndex() {
  const [dashboard, setDashboard] = useState<any>();
  const [growth, setGrowth] = useState<QuestionGrowthData>(EMPTY_GROWTH);
  const [range, setRange] = useState<RangeKey>("12m");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  const getDashboard = async (period: RangeKey, isInitial: boolean) => {
    isInitial ? setLoading(true) : setChartLoading(true);
    await DashboardService.getCompanyDashboardStats(period)
      .then((res) => {
        if (isInitial) setDashboard(res.data);
        setGrowth(mapGrowthResponse(res.data?.questionLibraryGrowth));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        isInitial ? setLoading(false) : setChartLoading(false);
      });
  };

  useEffect(() => {
    getDashboard("12m", true);
  }, []);

  const handleRangeChange = (next: RangeKey) => {
    setRange(next);
    getDashboard(next, false);
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5">
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} height={120} borderRadius={16} />
            ))}
        </div>
        <Skeleton height={300} borderRadius={16} />
        <div className="tw-grid tw-gap-4 tw-grid-cols-1 lg:tw-grid-cols-2">
          <Skeleton height={240} borderRadius={16} />
          <Skeleton height={240} borderRadius={16} />
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats ?? {};
  const breakdown = dashboard?.verificationBreakdown ?? {};
  const activity = dashboard?.recentActivity ?? {};

  const total = Number(stats.total ?? 0);
  const verified = Number(stats.verified ?? 0);
  const toVerify = Number(stats.toVerify ?? 0);
  const inReview = Number(stats.inReview ?? 0);
  const questionPapers = Number(stats.questionPapers ?? 0);
  const completion = Number(stats.verifiedPercent ?? 0);

  const kpis = [
    {
      title: "Total Questions",
      value: total,
      icon: faLayerGroup,
      color: "#2563EB",
      pill: "Library",
      pillTone: "neutral",
    },
    {
      title: "Verified",
      value: verified,
      icon: faCircleCheck,
      color: "#16A34A",
      pill: `${stats.verifiedPercent ?? 0}% of total`,
      pillTone: "ok",
    },
    {
      title: "To Verify",
      value: toVerify,
      icon: faClock,
      color: "#D97706",
      pill: `${stats.toVerifyPercent ?? 0}% of total`,
      pillTone: "warn",
    },
    {
      title: "In Review",
      value: inReview,
      icon: faMagnifyingGlass,
      color: "#0891B2",
      pill: `${stats.inReviewPercent ?? 0}% of total`,
      pillTone: "info",
    },
    {
      title: "Question Papers",
      value: questionPapers,
      icon: faFileLines,
      color: "#7C3AED",
      pill: "Generated",
      pillTone: "neutral",
    },
  ];

  const pillStyle: any = {
    neutral: { color: "#64748B", background: "#f1f5f9" },
    ok: { color: "#16A34A", background: "#ECFDF5" },
    warn: { color: "#D97706", background: "#FFFBEB" },
    info: { color: "#0891B2", background: "#ECFEFF" },
  };

  // Recent activity summary, straight from the API's recentActivity block.
  const recentActivity = [
    {
      label: "Verified questions",
      value: Number(activity.verifiedQuestions ?? 0),
      color: "#16A34A",
      icon: faCircleCheck,
    },
    {
      label: "In review",
      value: Number(activity.inReview ?? 0),
      color: "#0891B2",
      icon: faMagnifyingGlass,
    },
    {
      label: "Awaiting verification",
      value: Number(activity.awaitingVerification ?? 0),
      color: "#D97706",
      icon: faClock,
    },
  ];

  const quickActions = [
    {
      to: "/dash/view",
      label: "View Questions",
      icon: faEye,
      color: "#2563EB",
    },
    {
      to: "/dash/upload",
      label: "Upload Questions",
      icon: faUpload,
      color: "#0891B2",
    },
    {
      to: "/dash/qp_generation",
      label: "Create Question Paper",
      icon: faPlus,
      color: "#16A34A",
    },
    {
      to: "/dash/ai-generator",
      label: "AI Generator",
      icon: faBrain,
      color: "#D97706",
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {/* 1 — KPI row */}
      <div className="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5">
        {kpis.map((k) => (
          <div key={k.title} className="ds-kpi">
            <span
              className="ds-kpi__accent"
              style={{
                background: `linear-gradient(90deg, ${k.color}, ${k.color}66)`,
              }}
            />
            <div className="tw-flex tw-items-center tw-justify-between">
              <span
                className="ds-kpi__icon"
                style={{ background: `${k.color}14`, color: k.color }}
              >
                <FontAwesomeIcon icon={k.icon} />
              </span>
              <span className="ds-pill" style={pillStyle[k.pillTone]}>
                {k.pill}
              </span>
            </div>
            <div className="ds-kpi-value tw-mt-3" style={{ fontSize: 26 }}>
              {k.value}
            </div>
            <div className="ds-eyebrow tw-mt-1">{k.title}</div>
          </div>
        ))}
      </div>

      {/* 3 — Question Library Growth (Added vs Verified trend) */}
      <QuestionGrowthChart
        data={growth}
        loading={chartLoading}
        range={range}
        onRangeChange={handleRangeChange}
      />

      {/* 4 — Donut + Recent Activity */}
      <div className="tw-grid tw-gap-4 tw-grid-cols-1 lg:tw-grid-cols-2">
        <div className="ds-card" style={{ padding: 24 }}>
          <div className="ds-section-title tw-mb-1">Verification Breakdown</div>
          <div className="ds-section-sub tw-mb-5">
            Distribution across the question library
          </div>
          <DonutChart
            size={176}
            thickness={22}
            centerLabel={`${completion}%`}
            centerSub="verified"
            segments={[
              {
                label: "Verified",
                value: Number(breakdown.verified ?? 0),
                color: "#16A34A",
              },
              {
                label: "To Verify",
                value: Number(breakdown.toVerify ?? 0),
                color: "#D97706",
              },
              {
                label: "In Review",
                value: Number(breakdown.inReview ?? 0),
                color: "#0891B2",
              },
            ]}
          />
        </div>

        <div className="ds-card" style={{ padding: 24 }}>
          <div className="ds-section-title tw-mb-1">
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              className="tw-mr-2"
              style={{ color: "#64748B" }}
            />
            Recent Activity
          </div>
          <div className="ds-section-sub tw-mb-3">
            Verification pipeline summary
          </div>
          <div>
            {recentActivity.map((item, i) => (
              <div key={i} className="ds-activity-row">
                <span
                  className="ds-activity-dot"
                  style={{ background: `${item.color}14`, color: item.color }}
                >
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <div
                  className="tw-flex-1"
                  style={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}
                >
                  {item.label}
                </div>
                <span className="ds-kpi-value" style={{ fontSize: 18 }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 5 — Quick Actions */}
      <div className="ds-card" style={{ padding: 24 }}>
        <div className="ds-section-title tw-mb-4">Quick Actions</div>
        <div className="tw-grid tw-gap-3 tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
          {quickActions.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              className="ds-card-hover tw-flex tw-items-center tw-gap-3 tw-no-underline"
              style={{
                border: "1px solid var(--ds-border)",
                borderRadius: 12,
                padding: 16,
                color: "var(--ds-text)",
              }}
            >
              <span
                className="tw-flex tw-items-center tw-justify-center"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: `${a.color}14`,
                  color: a.color,
                }}
              >
                <FontAwesomeIcon icon={a.icon} />
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                {a.label}
              </span>
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{ color: "var(--ds-text-faint)", fontSize: 12 }}
              />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
